import { InputLabel, Stack } from '@components/ui';
import { InputAdornment, makeStyles, Slider, SliderProps, TextField, TextFieldProps } from '@material-ui/core';
import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { replaceItemAtIndex } from 'src/modules/util';

import type { Merge } from 'type-fest';

export type RangeSliderProps = {
  label: string;
  onChange: (value: [min: number | '', max: number | '']) => void;
  value: (number | '')[] | undefined;
  defaultValue?: number[];
  unit?: React.ReactNode;
  /**
   * Leave the number inputs empty while the filter is unset instead of pre-filling them with
   * min/max, so an inactive filter reads the same way as in CurrencySliderInput. The slider track
   * still spans the full range.
   *
   * This also changes how edits are handled: a bound the user has not typed stays `''` rather than
   * being committed as the slider end.
   */
  blankWhenUnset?: boolean;
};

type NumberFormatCustomProps = {
  inputRef: (instance: NumberFormat<any> | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
};

type RangeSliderInputProps = TextFieldProps & {
  unit?: React.ReactNode;
};

const useClasses = makeStyles((theme) => ({
  inputLabel: {
    marginBlockEnd: theme.spacing(0.5),
    fontWeight: 'normal',
    fontSize: 12,
    color: theme.palette.grey[600],
  },
}));

/**
 * A blank bound means "unbounded on that side", so the thumb belongs at the matching end of the
 * track. Number('') is 0, which only happens to be that end while min is 0 — the price change
 * filters are the first with a negative range, where it parks the lower thumb past the upper one
 * and inverts the track. Non-finite values are mapped too so the slider can never receive NaN.
 */
const toSliderBound = (bound: number | '' | undefined, fallback: number): number =>
  bound === '' || bound === undefined || !Number.isFinite(bound) ? fallback : bound;

const NumberFormatCustom = ({ inputRef, onChange, name, ...rest }: NumberFormatCustomProps) => (
  <NumberFormat
    {...rest}
    name={name}
    getInputRef={inputRef}
    onValueChange={(values, sourceInfo) => {
      // v4 also fires this when the value prop changes programmatically — resetting the filter
      // would otherwise echo an empty value back and re-activate it with a single bound. Only
      // user edits may reach the form.
      if (sourceInfo?.source === 'prop') return;
      onChange({
        target: {
          name,
          value: values.value,
        },
      });
    }}
    isNumericString
    decimalSeparator=","
    thousandSeparator="."
  />
);

const RangeSliderInput = ({ unit, ...props }: RangeSliderInputProps) => (
  <TextField
    {...props}
    variant="outlined"
    size="small"
    InputProps={{
      endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : undefined,
      inputComponent: NumberFormatCustom as any,
    }}
  />
);

export const RangeSlider = ({
  label,
  min = 0,
  max = 100,
  defaultValue: initialDefaultValue,
  onChange,
  value,
  name,
  step,
  unit,
  blankWhenUnset,
  ...rest
}: Merge<Omit<SliderProps<'span'>, 'value'>, RangeSliderProps>): JSX.Element => {
  const classes = useClasses();
  const { t } = useTranslation();
  const defaultValue = initialDefaultValue ?? (min !== undefined && max !== undefined ? [min, max] : undefined);
  const [startValue, endValue] = value ?? defaultValue ?? [];
  const [startInput, endInput] = value ?? (blankWhenUnset ? ['', ''] : (defaultValue ?? []));

  const handleSliderChange = (_event: ChangeEvent<{}>, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) return;

    const [nextStart, nextEnd] = newValue;
    // A bound the user left blank is drawn at the end of the track, so the slider reports that end
    // back for it. Keep it blank unless the thumb actually moved away, otherwise dragging one thumb
    // silently commits the opposite bound the user never set.
    onChange([
      startInput === '' && nextStart === min ? '' : nextStart,
      endInput === '' && nextEnd === max ? '' : nextEnd,
    ]);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: 0 | 1) => {
    // Partial input such as a lone "-" cannot be a bound yet. Treat it as "unbounded on this side"
    // rather than committing NaN or keeping the previous number — the latter left the field and the
    // applied filter disagreeing after a backspace. Programmatic echoes never get here; they are
    // filtered by source in NumberFormatCustom.
    const raw = event.target.value;
    const parsed = raw === '' || !Number.isFinite(Number(raw)) ? '' : Number(raw);

    // Built from the displayed inputs, not from the slider fallbacks, so editing one bound does not
    // silently commit the default for the other one while the filter is still blank.
    onChange(replaceItemAtIndex([startInput, endInput], index, parsed) as [min: number | '', max: number | '']);
  };

  return (
    <div>
      <InputLabel className={classes.inputLabel}>{t(label as any)}</InputLabel>
      <Slider
        name={name}
        min={min}
        max={max}
        step={step}
        defaultValue={defaultValue}
        color="secondary"
        style={{
          display: 'block',
        }}
        valueLabelDisplay="off"
        onChange={handleSliderChange}
        value={[toSliderBound(startValue, min), toSliderBound(endValue, max)]}
        {...rest}
      />
      <Stack direction="row" spacing={1}>
        <RangeSliderInput value={startInput ?? ''} onChange={(event) => handleInputChange(event, 0)} unit={unit} />
        <RangeSliderInput value={endInput ?? ''} onChange={(event) => handleInputChange(event, 1)} unit={unit} />
      </Stack>
    </div>
  );
};
